/**
 * Поиск бренда — в шапке таблицы, в блоке «Бренд».
 *
 * Раньше он стоял в верхней строке приложения, далеко от столбца с
 * названиями. Здесь он стоит ровно над тем, что ищет, и уезжает вместе с
 * закреплённым блоком «Бренд» при прокрутке вбок.
 *
 * Значение живёт в адресе (`q`), а не в состоянии компонента: ссылку с
 * поиском можно переслать, и она откроется тем же срезом (§10.5). Локальная
 * копия нужна только чтобы ввод не ждал перерисовки таблицы.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { IconClose, IconSearch } from '../../../components/index.js'
import { FOCUS_SEARCH } from '../../../app/AppShell.js'
import { texts } from '../../../texts.js'
import './TableSearch.css'

export interface TableSearchProps {
  value: string
  onChange: (value: string) => void
}

/** Сколько держимся за фокус после последней буквы. */
const TYPING_GRACE_MS = 3_000

export function TableSearch({ value, onChange }: TableSearchProps) {
  const input = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState(value)
  /* Когда человек печатал в последний раз и где стоял курсор. */
  const typedAt = useRef(0)
  const caret = useRef<number | null>(null)

  useEffect(() => { setDraft(value) }, [value])

  /**
   * Не отдавать фокус перерисовке.
   *
   * Поле стоит внутри шапки таблицы, а таблица под ним перестраивается на
   * каждую букву: меняется набор строк, ширины, закреплённые блоки. Стоит
   * узлу поля пересобраться — фокус уходит в никуда (`document.body`), и
   * человек, набравший половину названия, обнаруживает, что дальше не
   * печатается, пока не ткнёшь в поле мышью.
   *
   * Возвращаем фокус только если он именно провалился в пустоту и только
   * первые секунды после набора: если человек сам ушёл в ячейку или в меню,
   * отбирать у него фокус нельзя.
   */
  useLayoutEffect(() => {
    const field = input.current
    if (!field) return
    if (document.activeElement === field) return
    if (Date.now() - typedAt.current > TYPING_GRACE_MS) return
    const active = document.activeElement
    if (active !== null && active !== document.body) return
    field.focus()
    const at = caret.current ?? field.value.length
    try { field.setSelectionRange(at, at) } catch { /* поле могло быть не текстовым */ }
  })

  /* «/» из любого места ставит фокус сюда: клавиша живёт в оболочке и не
     знает, где сейчас нарисовано поле, поэтому зовёт его событием. */
  useEffect(() => {
    function focus() {
      input.current?.focus()
      input.current?.select()
    }
    window.addEventListener(FOCUS_SEARCH, focus)
    return () => window.removeEventListener(FOCUS_SEARCH, focus)
  }, [])

  return (
    <form
      className="table-search"
      role="search"
      onSubmit={(event) => { event.preventDefault(); onChange(draft) }}
    >
      <span className="table-search__icon" aria-hidden="true"><IconSearch size={16} /></span>
      <input
        ref={input}
        className="table-search__input"
        /* Намеренно не type="search": свой крестик браузера встаёт поверх
           нашего и в каждом движке выглядит по-своему. */
        type="text"
        value={draft}
        placeholder={texts.topbar.searchPlaceholder}
        aria-label={texts.topbar.searchLabel}
        autoComplete="off"
        onChange={(event) => {
          typedAt.current = Date.now()
          caret.current = event.target.selectionStart
          setDraft(event.target.value)
          onChange(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && draft !== '') {
            event.preventDefault()
            event.stopPropagation()
            setDraft('')
            onChange('')
          }
        }}
      />
      {draft !== '' ? (
        <button
          type="button"
          className="table-search__clear"
          aria-label={texts.action.close}
          onClick={() => { setDraft(''); onChange(''); input.current?.focus() }}
        >
          <IconClose size={16} />
        </button>
      ) : null}
    </form>
  )
}
